﻿async function initShippingRates(root) {
  const endpoint = root.getAttribute('data-endpoint');
  const locationsUrl = root.getAttribute('data-shipping-rates-locations-url');
  const displayStyle = root.getAttribute('data-display-style') || 'popup';
  const isInline = displayStyle === 'inline';

  let strings = {};
  const stringsEl = root.querySelector('[data-shipping-rates-strings]');
  if (stringsEl?.textContent?.trim()) {
    try {
      strings = JSON.parse(stringsEl.textContent.trim());
    } catch {
      strings = {};
    }
  }

  // Drawer / Widget elements (popup mode only)
  const drawer = root.querySelector('[data-shipping-rates-drawer]');
  const openDrawerBtns = root.querySelectorAll('[data-shipping-rates-open-drawer]');
  const closeDrawerBtns = root.querySelectorAll('[data-shipping-rates-close-drawer]');

  const widgetEmpty = root.querySelector('[data-shipping-rates-widget-empty]');
  const widgetFilled = root.querySelector('[data-shipping-rates-widget-filled]');
  const widgetLoading = root.querySelector('[data-shipping-rates-widget-loading]');
  const widgetPrice = root.querySelector('[data-shipping-rates-widget-price]');
  const widgetLocation = root.querySelector('[data-shipping-rates-widget-location]');

  const inlineResults = root.querySelector('[data-shipping-rates-inline-results]');
  const inlineDeliveryLocation = root.querySelector('[data-shipping-rates-inline-delivery-location]');
  const inlineEditBtn = root.querySelector('[data-shipping-rates-inline-edit]');

  if (!isInline) {
    const openDrawer = () => {
      if (drawer) {
        drawer.hidden = false;
        setTimeout(() => drawer.classList.add('is-open'), 10);
        document.body.style.overflow = 'hidden';
      }
    };
    const closeDrawer = () => {
      if (drawer) {
        drawer.classList.remove('is-open');
        document.body.style.overflow = '';
        setTimeout(() => {
          if (!drawer.classList.contains('is-open')) {
            drawer.hidden = true;
          }
        }, 300);
      }
    };

    openDrawerBtns.forEach((btn) => btn.addEventListener('click', openDrawer));
    closeDrawerBtns.forEach((btn) => btn.addEventListener('click', closeDrawer));
  }

  // Form elements
  const cityDropdown = root.querySelector('[data-shipping-rates-city-dropdown]');
  const suburbDropdown = root.querySelector('[data-shipping-rates-suburb-dropdown]');
  const cityTrigger = cityDropdown.querySelector('[data-shipping-rates-dropdown-trigger]');
  const cityPanel = cityDropdown.querySelector('[data-shipping-rates-dropdown-panel]');
  const cityValue = cityDropdown.querySelector('[data-shipping-rates-dropdown-value]');
  const suburbTrigger = suburbDropdown.querySelector('[data-shipping-rates-dropdown-trigger]');
  const suburbPanel = suburbDropdown.querySelector('[data-shipping-rates-dropdown-panel]');
  const suburbValue = suburbDropdown.querySelector('[data-shipping-rates-dropdown-value]');
  
  const submitBtn = root.querySelector('[data-shipping-rates-submit]');
  const submitText = submitBtn?.querySelector('.shipping-rates__submit-text');
  const submitLoading = submitBtn?.querySelector('.shipping-rates__submit-loading');
  
  const errorWrap = root.querySelector('[data-shipping-rates-error]');
  const errorText = root.querySelector('.shipping-rates__error-text');
  const resultsEl = root.querySelector('[data-shipping-rates-results]');

  const estimatedCost = root.querySelector('[data-shipping-rates-estimated-cost]');
  const estimatedLoading = root.querySelector('[data-shipping-rates-estimated-loading]');

  let shippingContext = null;
  const contextEl = root.querySelector('[data-shipping-rates-context]');
  if (contextEl?.textContent?.trim()) {
    try {
      shippingContext = JSON.parse(contextEl.textContent.trim());
    } catch {
      shippingContext = null;
    }
  }

  const MIN_OPTIONS_FOR_SEARCH = 5;

  const compareAlpha = (a, b) =>
    a.localeCompare(b, 'en-NZ', { sensitivity: 'base', numeric: true });

  const toTitleCase = (str) => {
    if (str == null || str === '') return '';
    return String(str)
      .trim()
      .split(/\s+/)
      .map((word) => {
        if (!word.length) return '';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  const normalizeSearchText = (s) =>
    String(s ?? '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/\p{M}+/gu, '')
      .trim()
      .replace(/\s+/g, ' ');

  const buildLocKey = (loc) => (loc?.city ?? '');
  const parseLocKey = (key) => ({ city: key ?? '', delivery_branch: '' });

  let selectedLocKey = '';
  let selectedSubr = '';
  let isFetchingRates = false;
  const quotedFingerprints = new Set();

  const STORAGE_KEY = 'proferlo_sr_selection';

  const saveSelection = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ city: selectedLocKey, suburb: selectedSubr }));
    } catch {}
  };

  const loadSavedSelection = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.city === 'string' && typeof parsed.suburb === 'string') return parsed;
    } catch {}
    return null;
  };

  const CACHE_KEY = 'proferlo_sr_rates_cache';
  const CACHE_TTL = 30 * 60 * 1000;

  const getCachedRates = (fp) => {
    if (!fp) return null;
    try {
      const store = JSON.parse(localStorage.getItem(CACHE_KEY) ?? '{}');
      const entry = store?.[fp];
      if (!entry || !Array.isArray(entry.rates)) return null;
      if (Date.now() - entry.ts > CACHE_TTL) return null;
      return entry.rates;
    } catch {}
    return null;
  };

  const setCachedRates = (fp, rates) => {
    if (!fp) return;
    try {
      let store = {};
      try { store = JSON.parse(localStorage.getItem(CACHE_KEY) ?? '{}') ?? {}; } catch {}
      const now = Date.now();
      for (const key of Object.keys(store)) {
        if (now - (store[key]?.ts ?? 0) > CACHE_TTL) delete store[key];
      }
      store[fp] = { rates, ts: now };
      localStorage.setItem(CACHE_KEY, JSON.stringify(store));
    } catch {}
  };

  const getQuoteFingerprint = () => {
    const ctx = shippingContext;
    if (!ctx?.product_form_id || !selectedLocKey || !selectedSubr) return '';
    const vid = getVariantIdFromProductForm(ctx.product_form_id);
    const qty = getQuantityFromProductForm(ctx.product_form_id);
    return `${selectedLocKey}\u0002${selectedSubr}\u0002${vid ?? ''}\u0002${qty}`;
  };

  const syncResultsVisibility = () => {
    resultsEl.hidden = resultsEl.childElementCount === 0;
  };

  const getQuantityFromProductForm = (formId) => {
    if (!formId) return 1;
    const form = document.getElementById(formId);
    const input = form?.querySelector('input[name="quantity"]');
    const n = Number.parseInt(input?.value ?? '1', 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  };

  const getVariantIdFromProductForm = (formId) => {
    if (!formId) return null;
    const form = document.getElementById(formId);
    const input = form?.querySelector('input[name="id"]');
    if (!input?.value) return null;
    const n = Number(input.value);
    return Number.isFinite(n) ? n : null;
  };

  let locations = [];
  if (locationsUrl) {
    try {
      const res = await fetch(locationsUrl, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('shipping locations fetch failed');
      locations = await res.json();
    } catch {
      locations = [];
    }
  }

  const buildShippingRatesRequestBody = () => {
    const ctx = shippingContext;
    if (!ctx?.variants?.length) return null;

    const formId = ctx.product_form_id;
    const qty = getQuantityFromProductForm(formId);
    const vid = getVariantIdFromProductForm(formId);
    const variant =
      ctx.variants.find((v) => Number(v.id) === Number(vid)) ?? ctx.variants[0];

    const unitPrice = Number(variant.price);
    const compareAt =
      variant.compare_at_price != null
        ? Number(variant.compare_at_price)
        : unitPrice;
    const subtotal_price = compareAt * qty;
    const total_price = unitPrice * qty;
    const discount_amount = Math.max(0, subtotal_price - total_price);

    const locParts = parseLocKey(selectedLocKey);
    const pickedLoc = locations.find(
      (loc) => buildLocKey(loc) === selectedLocKey,
    );

    return {
      destination: {
        country: 'NZ',
        city: (locParts.city || '').toUpperCase(),
        address2: selectedSubr.toUpperCase(),
        ...(typeof pickedLoc?.region_zone === 'string' && pickedLoc.region_zone
          ? { region_zone: pickedLoc.region_zone }
          : {}),
      },
      items: [
        {
          name: variant.display_name,
          sku: variant.sku || '',
          quantity: qty,
          price: unitPrice,
          product_id: ctx.product_id,
          variant_id: variant.id,
        },
      ],
      currency: ctx.currency,
      locale: ctx.locale,
      order_totals: {
        subtotal_price,
        total_price,
        discount_amount,
      },
    };
  };

  const subrsByLocKey = {};
  for (const loc of locations) {
    if (!loc || typeof loc.city !== 'string' || !Array.isArray(loc.subrs))
      continue;
    const key = buildLocKey(loc);
    if (!subrsByLocKey[key]) subrsByLocKey[key] = [];
    for (const s of loc.subrs) {
      if (typeof s === 'string' && s && !subrsByLocKey[key].includes(s)) {
        subrsByLocKey[key].push(s);
      }
    }
  }

  const cityOptionLabel = (locKey) => toTitleCase(parseLocKey(locKey).city);

  const locationKeys = Object.keys(subrsByLocKey).sort(compareAlpha);

  for (const k of locationKeys) {
    subrsByLocKey[k].sort(compareAlpha);
  }

  const syncSubmitEnabled = () => {
    const ready = Boolean(selectedLocKey && selectedSubr);
    const fp = getQuoteFingerprint();
    const alreadyQuotedForSelection =
      ready && fp !== '' && quotedFingerprints.has(fp);
    submitBtn.disabled = isFetchingRates || !ready || alreadyQuotedForSelection;
    submitBtn.setAttribute(
      'aria-disabled',
      submitBtn.disabled ? 'true' : 'false',
    );
  };

  const closeDropdown = (dropdown) => {
    dropdown.classList.remove('is-open');
    const trigger = dropdown.querySelector('[data-shipping-rates-dropdown-trigger]');
    const panel = dropdown.querySelector('[data-shipping-rates-dropdown-panel]');
    trigger.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
  };

  const openDropdown = (dropdown) => {
    root.querySelectorAll('.shipping-rates__dropdown.is-open').forEach((d) => {
      if (d !== dropdown) closeDropdown(d);
    });
    dropdown.classList.add('is-open');
    const trigger = dropdown.querySelector('[data-shipping-rates-dropdown-trigger]');
    const panel = dropdown.querySelector('[data-shipping-rates-dropdown-panel]');
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    requestAnimationFrame(() => {
      const searchWrap = panel.querySelector('[data-shipping-rates-search-wrap]');
      const searchInput = panel.querySelector('[data-shipping-rates-search-input]');
      if (searchWrap && !searchWrap.hidden && searchInput) {
        searchInput.focus({ preventScroll: true });
      }
    });
  };

  const toggleDropdown = (dropdown) => {
    if (dropdown.classList.contains('is-open')) {
      closeDropdown(dropdown);
    } else {
      openDropdown(dropdown);
    }
  };

  const closeAllDropdowns = () => {
    root.querySelectorAll('.shipping-rates__dropdown.is-open').forEach(closeDropdown);
  };

  const hideError = () => {
    errorWrap.hidden = true;
    errorText.textContent = '';
  };

  const showError = (msg) => {
    errorText.textContent = msg;
    errorWrap.hidden = false;
  };

  const attachProductFormWatchers = () => {
    const formId = shippingContext?.product_form_id;
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form || form.__shippingRatesBound) return;
    form.__shippingRatesBound = true;
    const onVariantOrQtyChange = () => {
      hideError();
      syncSubmitEnabled();
    };
    form.addEventListener('change', onVariantOrQtyChange);
    form.addEventListener('input', (e) => {
      if (e.target?.name === 'quantity') onVariantOrQtyChange();
    });
  };

  const fillPanelOptions = (panelWrapper, values, onPick, opts) => {
    const formatLabel =
      opts && typeof opts.formatLabel === 'function'
        ? opts.formatLabel
        : (v) => toTitleCase(v);
    const matchSearch =
      opts && typeof opts.matchSearch === 'function'
        ? opts.matchSearch
        : (v, rawQ) => {
            const needle = normalizeSearchText(rawQ);
            if (!needle) return true;
            return normalizeSearchText(v).includes(needle);
          };
    const sortFiltered =
      opts && typeof opts.sortFiltered === 'function' ? opts.sortFiltered : null;

    const searchWrap = panelWrapper.querySelector('[data-shipping-rates-search-wrap]');
    const searchInput = panelWrapper.querySelector('[data-shipping-rates-search-input]');
    const listEl = panelWrapper.querySelector('[data-shipping-rates-options-list]');

    const sortedValues = [...values].sort(compareAlpha);
    panelWrapper._srValues = sortedValues;

    const renderList = (vals) => {
      listEl.replaceChildren();
      if (vals.length === 0) {
        if (panelWrapper._srValues.length > 0) {
          const emptyLi = document.createElement('li');
          emptyLi.className = 'shipping-rates__dropdown-empty';
          emptyLi.setAttribute('role', 'presentation');
          emptyLi.textContent = strings.searchNoResults;
          listEl.append(emptyLi);
        }
        return;
      }
      for (const value of vals) {
        const li = document.createElement('li');
        li.setAttribute('role', 'presentation');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'shipping-rates__dropdown-option';
        btn.setAttribute('role', 'option');
        btn.textContent = formatLabel(value);
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          onPick(value);
          closeDropdown(panelWrapper.closest('.shipping-rates__dropdown'));
        });
        li.append(btn);
        listEl.append(li);
      }
    };

    if (sortedValues.length > MIN_OPTIONS_FOR_SEARCH) {
      searchWrap.hidden = false;
      searchInput.value = '';
      renderList(sortedValues);
      searchInput.oninput = () => {
        const rawQ = searchInput.value;
        const needle = normalizeSearchText(rawQ);
        const filtered = needle
          ? panelWrapper._srValues.filter((v) => matchSearch(v, rawQ))
          : panelWrapper._srValues;
        const sortFn = sortFiltered
          ? (a, b) => sortFiltered(a, b, rawQ)
          : compareAlpha;
        renderList([...filtered].sort(sortFn));
      };
    } else {
      searchWrap.hidden = true;
      searchInput.value = '';
      searchInput.oninput = null;
      renderList(sortedValues);
    }
  };

  fillPanelOptions(
    cityPanel,
    locationKeys,
    (locKey) => {
      selectedLocKey = locKey;
      cityValue.textContent = cityOptionLabel(locKey);
      cityValue.removeAttribute('data-placeholder');
      hideError();
      fillSuburbsForCity(locKey);
    },
    {
      formatLabel: cityOptionLabel,
      matchSearch: (locKey, rawQ) => {
        const needle = normalizeSearchText(rawQ);
        if (!needle) return true;
        const hay = normalizeSearchText(cityOptionLabel(locKey));
        return hay.includes(needle);
      },
      sortFiltered: (a, b, rawQ) => {
        const needle = normalizeSearchText(rawQ);
        if (!needle) {
          return compareAlpha(cityOptionLabel(a), cityOptionLabel(b));
        }
        const rank = (lk) => {
          const lab = normalizeSearchText(cityOptionLabel(lk));
          if (lab === needle) return 0;
          if (lab.startsWith(needle)) return 2;
          const i = lab.indexOf(needle);
          if (i === -1) return 90000;
          return 80 + i;
        };
        const ra = rank(a);
        const rb = rank(b);
        if (ra !== rb) return ra - rb;
        return compareAlpha(cityOptionLabel(a), cityOptionLabel(b));
      }
    }
  );

  const resetSuburbUi = () => {
    selectedSubr = '';
    suburbValue.textContent = strings.suburbPlaceholder;
    suburbValue.setAttribute('data-placeholder', '');
    fillPanelOptions(suburbPanel, [], () => {});
  };

  const fillSuburbsForCity = (locKey) => {
    closeAllDropdowns();
    resetSuburbUi();
    if (!locKey) {
      suburbTrigger.disabled = true;
      syncSubmitEnabled();
      return;
    }
    const subs = subrsByLocKey[locKey] ?? [];
    const pickSuburb = (subr) => {
      selectedSubr = subr;
      suburbValue.textContent = toTitleCase(subr);
      suburbValue.removeAttribute('data-placeholder');
      hideError();
      syncSubmitEnabled();
    };
    fillPanelOptions(suburbPanel, subs, pickSuburb);
    suburbTrigger.disabled = subs.length === 0;
    if (subs.length === 1) {
      pickSuburb(subs[0]);
    } else {
      syncSubmitEnabled();
    }
  };

  cityTrigger.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleDropdown(cityDropdown);
  });

  suburbTrigger.addEventListener('click', (e) => {
    e.stopPropagation();
    if (suburbTrigger.disabled) return;
    toggleDropdown(suburbDropdown);
  });

  document.addEventListener('click', () => closeAllDropdowns());
  if (!isInline) {
    drawer?.addEventListener('click', () => closeAllDropdowns());
  }
  
  // Prevent closing dropdown when clicking inside it
  cityDropdown.addEventListener('click', e => e.stopPropagation());
  suburbDropdown.addEventListener('click', e => e.stopPropagation());

  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAllDropdowns();
  });

  const isValidRatesPayload = (data) =>
    Boolean(
      data &&
      typeof data === 'object' &&
      !Array.isArray(data) &&
      Array.isArray(data.rates) &&
      data.rates.every(
        (r) =>
          r &&
          typeof r === 'object' &&
          typeof r.service_name === 'string' &&
          typeof r.service_code === 'string' &&
          r.total_price != null &&
          typeof r.currency === 'string',
      ),
    );

  const formatShippingPrice = (minorUnits) => {
    const n = Number(minorUnits);
    if (!Number.isFinite(n)) return null;
    const dollarsExact = n / 100;
    const dollarsRoundedUp = Math.ceil(dollarsExact * 100) / 100;
    const num = dollarsRoundedUp.toLocaleString('en-NZ', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `$${num}`;
  };

  const buildCombinationCaption = () => {
    const ctx = shippingContext;
    const parts = [];
    if (selectedLocKey) parts.push(cityOptionLabel(selectedLocKey));
    if (selectedSubr) parts.push(toTitleCase(selectedSubr));
    const pickedLoc = locations.find(
      (loc) => buildLocKey(loc) === selectedLocKey,
    );
    if (
      pickedLoc &&
      typeof pickedLoc.region_zone === 'string' &&
      pickedLoc.region_zone
    ) {
      parts.push(pickedLoc.region_zone);
    }
    if (ctx?.variants?.length && ctx.product_form_id) {
      const vid = getVariantIdFromProductForm(ctx.product_form_id);
      const qty = getQuantityFromProductForm(ctx.product_form_id);
      const variant = ctx.variants.find((v) => Number(v.id) === Number(vid));
      if (variant?.display_name) {
        parts.push(
          qty > 1 ? `${variant.display_name} × ${qty}` : variant.display_name,
        );
      }
    }
    return parts.join(' · ');
  };

  const renderRates = (rates) => {
    const fp = getQuoteFingerprint();
    if (!fp) return;

    const sortedRates = [...rates].sort((a, b) => a.total_price - b.total_price);
    const cheapestPriceStr = formatShippingPrice(sortedRates[0].total_price);

    if (isInline) {
      if (inlineDeliveryLocation) {
        inlineDeliveryLocation.textContent = `${cityOptionLabel(selectedLocKey)}, ${toTitleCase(selectedSubr)}`;
      }
      if (inlineResults) inlineResults.hidden = false;
    } else {
      if (estimatedCost) {
        estimatedCost.textContent = cheapestPriceStr;
        estimatedCost.hidden = false;
      }
      if (estimatedLoading) {
        estimatedLoading.hidden = true;
      }

      if (widgetPrice) {
        widgetPrice.textContent = cheapestPriceStr;
      }
      if (widgetLocation) {
        widgetLocation.textContent = `${toTitleCase(selectedSubr)}, ${cityOptionLabel(selectedLocKey)}`;
      }

      if (widgetEmpty && widgetFilled) {
        widgetEmpty.hidden = true;
        widgetFilled.hidden = false;
      }
    }

    resultsEl.replaceChildren();

    const batch = document.createElement('div');
    batch.className = 'shipping-rates__batch';

    const inner = document.createElement('div');
    inner.className = 'shipping-rates__batch-inner';

    const comboCaption = buildCombinationCaption();
    const ul = document.createElement('ul');
    ul.className = 'shipping-rates__list subtitle';

    for (const r of sortedRates) {
      const price = formatShippingPrice(r.total_price);
      if (price === null) {
        showError(strings.errorInvalid);
        return;
      }
      const li = document.createElement('li');
      li.className = 'shipping-rates__item';

      const textCol = document.createElement('div');
      textCol.className = 'shipping-rates__item-text';

      if (isInline) {
        const radio = document.createElement('span');
        radio.className = 'shipping-rates__inline-radio';
        radio.setAttribute('aria-hidden', 'true');
        textCol.append(radio);
      }

      const service = document.createElement('span');
      service.className = 'shipping-rates__service';
      service.textContent = (isInline && strings.rateNameOverride) ? strings.rateNameOverride : r.service_name;

      if (!isInline) {
        const combo = document.createElement('span');
        combo.className = 'shipping-rates__combo';
        combo.textContent = comboCaption;
        textCol.append(service, combo);
      } else {
        textCol.append(service);
      }

      const priceEl = document.createElement('span');
      priceEl.className = 'shipping-rates__price';
      priceEl.textContent = price;

      li.append(textCol, priceEl);
      ul.append(li);
    }

    inner.append(ul);
    batch.append(inner);
    resultsEl.prepend(batch);

    quotedFingerprints.add(fp);
    saveSelection();
    syncResultsVisibility();
    syncSubmitEnabled();
  };

  suburbTrigger.disabled = true;
  attachProductFormWatchers();
  syncSubmitEnabled();

  const performFetch = async (opts = {}) => {
    const { silent = false } = opts;

    if (!selectedLocKey || !selectedSubr) return;

    const fp = getQuoteFingerprint();
    const cached = getCachedRates(fp);
    if (cached) {
      renderRates(cached);
      return;
    }

    isFetchingRates = true;
    syncSubmitEnabled();

    if (silent && widgetLoading) {
      widgetLoading.hidden = false;
    }

    if (submitText && submitLoading) {
      submitText.hidden = true;
      submitLoading.hidden = false;
    }

    if (!isInline && estimatedCost && estimatedLoading) {
      estimatedCost.hidden = true;
      estimatedLoading.hidden = false;
    }

    if (isInline && inlineResults) inlineResults.hidden = true;
    resultsEl.replaceChildren();
    syncResultsVisibility();

    try {
      const body = buildShippingRatesRequestBody();
      if (!body) {
        if (!silent) showError(strings.errorInvalid);
        return;
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      const text = await res.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch { data = null; }

      if (!res.ok || !isValidRatesPayload(data)) throw new Error('invalid');

      if (!data.rates.length) {
        if (!silent) showError(strings.errorInvalid);
        return;
      }

      renderRates(data.rates);
      setCachedRates(fp, data.rates);
    } catch (err) {
      if (!silent) {
        const isInvalid = err instanceof Error && err.message === 'invalid';
        showError(isInvalid ? strings.errorInvalid : strings.errorNetwork);
      }
    } finally {
      isFetchingRates = false;
      if (widgetLoading) widgetLoading.hidden = true;
      if (submitText && submitLoading) {
        submitText.hidden = false;
        submitLoading.hidden = true;
      }
      if (!isInline && estimatedCost && estimatedLoading) {
        estimatedCost.hidden = false;
        estimatedLoading.hidden = true;
      }
      syncSubmitEnabled();
    }
  };

  submitBtn?.addEventListener('click', () => {
    hideError();
    closeAllDropdowns();
    void performFetch();
  });

  inlineEditBtn?.addEventListener('click', () => {
    if (inlineResults) inlineResults.hidden = true;
    resultsEl.replaceChildren();
    syncResultsVisibility();
    const fp = getQuoteFingerprint();
    if (fp) quotedFingerprints.delete(fp);
    syncSubmitEnabled();
    hideError();
  });

  // Auto-restore saved selection from localStorage and fetch rates silently
  const saved = loadSavedSelection();
  if (saved) {
    const { city: savedCity, suburb: savedSuburb } = saved;
    const citySuburbs = subrsByLocKey[savedCity] ?? [];
    if (locationKeys.includes(savedCity) && citySuburbs.includes(savedSuburb)) {
      selectedLocKey = savedCity;
      cityValue.textContent = cityOptionLabel(savedCity);
      cityValue.removeAttribute('data-placeholder');

      fillSuburbsForCity(savedCity); // populates suburb panel; resets selectedSubr internally

      selectedSubr = savedSuburb;
      suburbValue.textContent = toTitleCase(savedSuburb);
      suburbValue.removeAttribute('data-placeholder');
      suburbTrigger.disabled = false;

      syncSubmitEnabled();
      void performFetch({ silent: true });
    }
  }
}

function bootShippingRates() {
  if (!window.__shippingRatesSeenRoots) {
    window.__shippingRatesSeenRoots = new WeakSet();
  }
  const seen = window.__shippingRatesSeenRoots;

  document.querySelectorAll('[data-shipping-rates-root]').forEach((root) => {
    if (seen.has(root)) return;
    seen.add(root);
    void initShippingRates(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootShippingRates);
} else {
  bootShippingRates();
}
